import React, { useEffect, useState } from "react";
import { Routes, Route, Link, useNavigate, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { api, formatErr } from "@/lib/api";
import { LayoutDashboard, Calendar, Users, PawPrint, Heart, GraduationCap, LogOut, Settings } from "lucide-react";
import { toast } from "sonner";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-forest-900">Se încarcă...</div>;
  if (!user) return <Navigate to="/admin/login" replace />;
  return children;
}

function Login() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  useEffect(() => { if (user) nav("/admin/dashboard"); }, [user, nav]);
  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try { await login(email, password); nav("/admin/dashboard"); }
    catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };
  return (
    <div className="min-h-screen bg-forest-900 flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-cream-50 rounded-2xl p-8 w-full max-w-md">
        <h1 className="font-serif text-2xl text-forest-900">Admin — Habitatul Meu</h1>
        <p className="text-sm text-muted mt-1">Autentificare pentru administratori</p>
        <div className="mt-6 space-y-3">
          <input data-testid="admin-email" type="email" required placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} className="w-full rounded-lg border border-forest-900/20 px-4 py-3 bg-white" />
          <input data-testid="admin-password" type="password" required placeholder="Parolă" value={password} onChange={e => setPassword(e.target.value)} className="w-full rounded-lg border border-forest-900/20 px-4 py-3 bg-white" />
          {err && <div className="text-sm text-red-600">{err}</div>}
          <button data-testid="admin-login-submit" disabled={loading} className="w-full bg-turaco text-white rounded-lg py-3 font-semibold disabled:opacity-40">{loading ? "..." : "Intră în cont"}</button>
        </div>
      </form>
    </div>
  );
}

function Sidebar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const items = [
    { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/admin/tururi", label: "Tururi", icon: Calendar },
    { to: "/admin/rezervari", label: "Rezervări", icon: Users },
    { to: "/admin/animale", label: "Animale", icon: PawPrint },
    { to: "/admin/scoli", label: "Școli", icon: GraduationCap },
    { to: "/admin/donatii", label: "Donații", icon: Heart },
    { to: "/admin/setari", label: "Setări", icon: Settings },
  ];
  return (
    <aside className="w-60 bg-forest-900 text-cream-50 min-h-screen flex flex-col">
      <div className="p-5 border-b border-cream-50/10">
        <div className="font-serif text-xl">Habitatul Meu</div>
        <div className="text-xs text-cream-50/60">{user?.email}</div>
      </div>
      <nav className="flex-1 p-3 space-y-1">
        {items.map(i => (
          <Link key={i.to} to={i.to} data-testid={`admin-nav-${i.to.split("/").pop()}`} className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${loc.pathname === i.to ? "bg-tucan text-white" : "text-cream-50/80 hover:bg-cream-50/10"}`}>
            <i.icon className="w-4 h-4" /> {i.label}
          </Link>
        ))}
      </nav>
      <button onClick={async () => { await logout(); nav("/admin/login"); }} data-testid="admin-logout" className="m-3 flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-cream-50/80 hover:bg-cream-50/10"><LogOut className="w-4 h-4" /> Ieșire</button>
    </aside>
  );
}

function AdminShell({ children }) {
  return (
    <div className="min-h-screen bg-cream-100 flex">
      <Sidebar />
      <main className="flex-1 p-6 lg:p-10 overflow-x-auto">{children}</main>
    </div>
  );
}

function Dashboard() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then(r => setStats(r.data)).catch(() => {}); }, []);
  const s = stats || {};
  const cards = [
    { l: "Tururi viitoare", v: s.upcoming_tours || 0 },
    { l: "Rezervări total", v: s.total_bookings || 0 },
    { l: "Plăți în așteptare", v: s.pending_bookings || 0 },
    { l: "Cereri școli noi", v: s.school_requests_new || 0 },
    { l: "Animale publicate", v: s.animals_count || 0 },
    { l: "Donații", v: s.donations_count || 0 },
  ];
  return (
    <div>
      <h1 className="font-serif text-3xl text-forest-900">Dashboard</h1>
      <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-4">
        {cards.map(c => (
          <div key={c.l} className="bg-white rounded-xl p-5 border border-forest-900/10">
            <div className="text-xs text-muted uppercase font-semibold">{c.l}</div>
            <div className="mt-2 font-serif text-4xl text-forest-900">{c.v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AdminTable({ title, endpoint, columns, testid }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get(endpoint).then(r => setRows(r.data)).catch(() => {}); }, [endpoint]);
  return (
    <div>
      <h1 className="font-serif text-3xl text-forest-900 mb-6">{title}</h1>
      <div className="bg-white rounded-xl border border-forest-900/10 overflow-x-auto" data-testid={testid}>
        <table className="w-full text-sm">
          <thead className="bg-cream-100"><tr>{columns.map(c => <th key={c.k} className="text-left px-4 py-3 font-semibold text-forest-900">{c.l}</th>)}</tr></thead>
          <tbody>{rows.map((r, i) => <tr key={i} className="border-t border-forest-900/5">{columns.map(c => <td key={c.k} className="px-4 py-3">{c.render ? c.render(r) : String(r[c.k] ?? "")}</td>)}</tr>)}</tbody>
        </table>
        {rows.length === 0 && <div className="p-6 text-center text-muted">Niciun rezultat.</div>}
      </div>
    </div>
  );
}

const ToursAdmin = () => <AdminTable title="Tururi" endpoint="/admin/tours" testid="admin-tours-table" columns={[
  { k: "date", l: "Data" }, { k: "start_time", l: "Ora" }, { k: "title", l: "Titlu" },
  { k: "capacity_reserved", l: "Rezervate", render: r => `${r.capacity_reserved}/${r.capacity_total}` },
  { k: "status", l: "Status" },
]} />;

const BookingsAdmin = () => <AdminTable title="Rezervări" endpoint="/admin/bookings" testid="admin-bookings-table" columns={[
  { k: "public_code", l: "Cod" }, { k: "customer_name", l: "Nume" }, { k: "phone", l: "Telefon" },
  { k: "tour_date", l: "Data tur" }, { k: "tour_time", l: "Ora" }, { k: "seats_total", l: "Locuri" },
  { k: "total", l: "Total" }, { k: "status", l: "Status" },
]} />;

const AnimalsAdmin = () => <AdminTable title="Animale" endpoint="/admin/animals" testid="admin-animals-table" columns={[
  { k: "name", l: "Nume" }, { k: "common_name", l: "Specie" }, { k: "category", l: "Categorie" },
  { k: "featured", l: "Featured", render: r => r.featured ? "Da" : "-" }, { k: "status", l: "Status" },
]} />;

const SchoolsAdmin = () => <AdminTable title="Solicitări școli" endpoint="/admin/school-requests" testid="admin-schools-table" columns={[
  { k: "institution", l: "Instituție" }, { k: "contact_name", l: "Contact" }, { k: "phone", l: "Telefon" },
  { k: "kids_estimate", l: "Copii" }, { k: "preferred_dates", l: "Perioadă" }, { k: "status", l: "Status" },
]} />;

const DonationsAdmin = () => <AdminTable title="Donații" endpoint="/admin/donations" testid="admin-donations-table" columns={[
  { k: "donor_name", l: "Donator" }, { k: "amount", l: "Sumă" }, { k: "recurring", l: "Recurentă", render: r => r.recurring ? "Da" : "-" },
  { k: "is_adoption", l: "Adopție", render: r => r.is_adoption ? "Da" : "-" }, { k: "payment_status", l: "Status" },
]} />;

function SettingsPage() {
  const [s, setS] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/settings").then(r => setS(r.data)).catch(() => {}); }, []);
  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/settings", s); toast.success("Setări salvate"); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div>
      <h1 className="font-serif text-3xl text-forest-900 mb-6">Setări</h1>
      <div className="bg-white rounded-xl p-6 border border-forest-900/10 space-y-3 max-w-2xl">
        {["site_name", "site_descriptor", "phone", "whatsapp", "address", "email", "facebook", "instagram", "cui", "status_banner_override"].map(k => (
          <div key={k}>
            <label className="text-xs uppercase font-semibold text-muted">{k}</label>
            <input value={s[k] || ""} onChange={e => setS({...s, [k]: e.target.value})} className="w-full rounded-lg border border-forest-900/20 px-4 py-2 bg-white" />
          </div>
        ))}
        <button data-testid="settings-save" disabled={saving} onClick={save} className="bg-turaco text-white rounded-lg px-6 py-2 font-semibold">{saving ? "..." : "Salvează"}</button>
      </div>
    </div>
  );
}

export default function AdminApp() {
  return (
    <AuthProvider>
      <Routes>
        <Route path="login" element={<Login />} />
        <Route path="dashboard" element={<Protected><AdminShell><Dashboard /></AdminShell></Protected>} />
        <Route path="tururi" element={<Protected><AdminShell><ToursAdmin /></AdminShell></Protected>} />
        <Route path="rezervari" element={<Protected><AdminShell><BookingsAdmin /></AdminShell></Protected>} />
        <Route path="animale" element={<Protected><AdminShell><AnimalsAdmin /></AdminShell></Protected>} />
        <Route path="scoli" element={<Protected><AdminShell><SchoolsAdmin /></AdminShell></Protected>} />
        <Route path="donatii" element={<Protected><AdminShell><DonationsAdmin /></AdminShell></Protected>} />
        <Route path="setari" element={<Protected><AdminShell><SettingsPage /></AdminShell></Protected>} />
        <Route path="*" element={<Navigate to="/admin/login" replace />} />
      </Routes>
    </AuthProvider>
  );
}
